import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { specialList } from "@/mocks/special";

export default function SpecialPage() {
  useDocumentMeta({
    title: "张柏芝特别专题 - 深度报道与年度合集",
    description:
      "以专题形式整理的张柏芝深度报道与年度合集，涵盖影视角色回顾、写真精选、公益纪实与港片回望等主题。",
    keywords: "张柏芝专题,张柏芝深度报道,影视回顾,写真合集,公益纪实",
  });

  const [featured, ...others] = specialList;

  return (
    <SiteLayout>
      <PageHero
        kicker="Special Features"
        title="特别专题"
        description="把值得慢慢读的内容整理成合集：作品回顾、写真精选、公益纪实与影史观察，每一个专题都按线索分组，方便按顺序阅读。"
      >
        <Breadcrumb items={[{ label: "特别专题" }]} />
      </PageHero>

      <div className="w-full px-4 py-10 md:px-8 md:py-14 lg:px-12">
        <Link
          to={`/special/${featured.id}`}
          className="group relative flex min-h-[360px] flex-col justify-end overflow-hidden rounded-lg bg-foreground-950 md:min-h-[420px]"
        >
          <img
            src={featured.cover}
            alt={`${featured.title} - 张柏芝专题封面`}
            title={`${featured.title} - 张柏芝专题`}
            className="absolute inset-0 h-full w-full object-cover object-top opacity-90 transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/50 to-foreground-950/10"></div>
          <div className="relative p-5 md:p-9">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-[11px] text-background-50">
                {featured.kicker}
              </span>
              <span className="rounded-full border border-background-50/35 px-3 py-1 text-[11px] text-background-100">
                {featured.period}
              </span>
              <span className="rounded-full border border-background-50/35 px-3 py-1 text-[11px] text-background-100">
                {featured.sections.length} 个章节 · {featured.articleCount} 篇内容
              </span>
            </div>
            <h2 className="mt-4 max-w-3xl font-heading text-2xl font-semibold leading-snug text-background-50 md:text-[34px]">
              {featured.title}
            </h2>
            <p className="mt-3 max-w-2xl text-[13px] leading-7 text-background-200 md:text-sm">{featured.intro}</p>
            <span className="mt-5 inline-flex items-center gap-2 rounded-md bg-background-50 px-4 py-2 text-xs text-foreground-900 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
              进入专题
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </span>
          </div>
        </Link>

        <div className="mt-10 flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">更多专题</h2>
          <span className="text-[11px] text-foreground-500">专题之间可互相切换浏览</span>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {others.map((item) => (
            <Link
              key={item.id}
              to={`/special/${item.id}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
            >
              <div className="h-44 w-full overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title} - 张柏芝专题封面`}
                  title={`${item.title} - 张柏芝专题`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="rounded-sm bg-secondary-200/70 px-2 py-0.5 text-secondary-900">{item.tag}</span>
                  <span className="text-foreground-500">{item.kicker}</span>
                </div>
                <h3 className="mt-2.5 font-heading text-base font-semibold leading-7 text-foreground-950 transition-colors group-hover:text-primary-700">
                  {item.title}
                </h3>
                <p className="mt-2 line-clamp-3 text-[13px] leading-6 text-foreground-600">{item.intro}</p>
                <div className="mt-auto flex items-center justify-between pt-4 text-[11px] text-foreground-500">
                  <span>{item.period}</span>
                  <span className="flex items-center gap-1 text-primary-700">
                    {item.articleCount} 篇内容
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </SiteLayout>
  );
}